import { forwardRef, useImperativeHandle } from 'react';

import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import { z } from 'zod';

import { Form, FormField } from '@/components';

import { useAccountData } from './AccountDataContext';

const additionalInfoItemSchema = z.object({
  obstacleType: z.string().optional(),
  obstacleRate: z.coerce
    .number({
      invalid_type_error: t('common:account.validation.invalidNumber'),
    })
    .min(0, t('common:account.validation.valueMinError'))
    .max(100, t('common:account.validation.valueMaxError'))
    .optional(),
  hasReport: z.boolean().optional().nullable().default(null),
});

const additionalInfoSchema = z.object({
  additionalInfo: z.array(additionalInfoItemSchema),
});

export type AdditionalInfoFormFields = z.infer<typeof additionalInfoSchema>;
export type AdditionalInfoItemFormFields = z.infer<
  typeof additionalInfoItemSchema
>;

export interface AdditionalInfoFormRef {
  submit: () => void;
}

export const AdditionalInfoForm = forwardRef<AdditionalInfoFormRef>(
  (_props, ref) => {
    const { t } = useTranslation(['common', 'candidate', 'cv', 'components']);
    const { data, updateAdditionalInfo } = useAccountData();

    // Create proper default additional info item
    const createDefaultAdditionalInfo = (): AdditionalInfoItemFormFields => ({
      obstacleType: '',
      obstacleRate: undefined,
      hasReport: null,
    });

    // Transform API data to form format
    const getFormDefaultValues = (): AdditionalInfoFormFields => {
      if (!data?.additionalInfo?.length) {
        return {
          additionalInfo: [createDefaultAdditionalInfo()],
        };
      }

      const transformedAdditionalInfo = data.additionalInfo.map((info) => ({
        obstacleType: info.obstacleType ?? '',
        obstacleRate: info.obstacleRate ? Number(info.obstacleRate) : undefined,
        hasReport: info.hasReport ?? null,
      }));

      return { additionalInfo: transformedAdditionalInfo };
    };

    const form = useForm<AdditionalInfoFormFields>({
      mode: 'onBlur',
      resolver: zodResolver(additionalInfoSchema),
      values: getFormDefaultValues(),
    });

    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: 'additionalInfo',
    });

    // Expose submit function to parent via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        form.handleSubmit(handleSubmit)();
      },
    }));

    const handleSubmit = async (formData: AdditionalInfoFormFields) => {
      // Transform data to match API format (convert number to string, null to undefined)
      const apiData = formData.additionalInfo.map((info) => ({
        ...info,
        obstacleRate: info.obstacleRate?.toString(),
        hasReport: info.hasReport ?? undefined,
      }));
      await updateAdditionalInfo(apiData);
    };

    const reportOptions = [
      { value: true, label: t('common:account.reportOptions.yes') },
      { value: false, label: t('common:account.reportOptions.no') },
    ];

    return (
      <Form {...form} onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {fields.map((field, idx) => (
            <Box key={field.id} borderBottom="1px solid #eee" pb={4}>
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontWeight="semibold" fontSize={16}>
                  {t('common:account.fields.additionalInfo.title')}{' '}
                  {fields.length > 1 ? idx + 1 : ''}
                </Text>
                <Button
                  onClick={() => append(createDefaultAdditionalInfo())}
                  colorScheme="brand"
                  variant="link"
                  leftIcon={
                    <Icon as={LuPlus} fontSize={20} color="brand.500" />
                  }
                >
                  {t('common:actions.addNew')}
                </Button>
              </Flex>

              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mt={4}
              >
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormField
                    control={form.control}
                    name={`additionalInfo.${idx}.obstacleType`}
                    label={t(
                      'common:account.fields.additionalInfo.obstacleType.label'
                    )}
                    placeholder={t(
                      'common:account.fields.additionalInfo.obstacleType.placeholder'
                    )}
                    type="text"
                    size={{ base: 'sm', md: 'md' }}
                  />
                </GridItem>
                <FormField
                  control={form.control}
                  name={`additionalInfo.${idx}.obstacleRate`}
                  label={t(
                    'common:account.fields.additionalInfo.obstacleRate.label'
                  )}
                  placeholder={t(
                    'common:account.fields.additionalInfo.obstacleRate.placeholder'
                  )}
                  type="number"
                  size={{ base: 'sm', md: 'md' }}
                />
                <FormField
                  control={form.control}
                  name={`additionalInfo.${idx}.hasReport`}
                  label={t(
                    'common:account.fields.additionalInfo.hasReport.label'
                  )}
                  placeholder={t(
                    'common:account.fields.additionalInfo.hasReport.placeholder'
                  )}
                  type="select"
                  options={reportOptions}
                  size={{ base: 'sm', md: 'md' }}
                />
              </Grid>

              {fields.length > 1 ? (
                <Button
                  mt={2}
                  variant="link"
                  color="red.500"
                  onClick={() => remove(idx)}
                  leftIcon={
                    <Icon as={LuTrash2} fontSize={20} color="red.500" />
                  }
                >
                  {t('common:actions.delete')}
                </Button>
              ) : (
                <Box />
              )}
            </Box>
          ))}
        </VStack>
      </Form>
    );
  }
);

AdditionalInfoForm.displayName = 'AdditionalInfoForm';
